/**
 * @file Require Role HOC.
 *
 * Check if the authenticated user has at least one of the required roles.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { MeWithRoles } from '../../graphql/queries/account';
import { hasRole } from '../../util/account';

/**
 * @param {React.Component} WrappedComponent The component to be composed.
 * @param {Array.<String>} roles The required user roles.
 *
 * @returns {React.Component} The composed component.
 */
const WithRoleHOC = (WrappedComponent, roles) =>
  class extends Component {
    static propTypes = {
      user: PropTypes.shape({
        roles: PropTypes.array,
      }),
      loading: PropTypes.bool.isRequired,
    };

    render() {
      const { user, loading } = this.props;

      if (loading) {
        return null;
      }

      if (!roles.some(role => hasRole(user, role))) {
        const redirectToError = {
          pathname: '/error',
          state: {
            error: 'You are not allowed to access this page',
          },
        };

        return <Redirect to={redirectToError} />;
      }

      return <WrappedComponent {...this.props} />;
    }
  };

/**
 * @param {Array.<String>} roles The required user roles.
 *
 * @returns {Function} - Higher order container which will decorate the passed
 * in component with all required data for the roles functionality.
 */
const RequireRole = roles => WrappedComponent => {
  const WithRole = WithRoleHOC(WrappedComponent, roles);

  const withUserData = graphql(MeWithRoles, {
    props: ({ data }) => ({
      loading: data.loading,
      user: data.me,
    }),
  });

  return compose(withUserData)(WithRole);
};

export { WithRoleHOC };
export default RequireRole;
